import {
  Autocomplete,
  Box,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { FilterCategories } from "~src/assets/data/filterCategories";
import { toTitleCase } from "~src/utils/string";

interface TransformedCategory {
  category_id: number;
  category_title: string;
  sub_category_title: string;
  sub_category_id: number;
}

const codes = transformData(FilterCategories);

const CategorySelect = ({
  onSelect,
  localValues,
  setLocalValues,
}: {
  onSelect: (value?: string[]) => void;
  localValues: TransformedCategory[];
  setLocalValues: React.Dispatch<React.SetStateAction<TransformedCategory[]>>;
}) => {
  const handleSectionHeaderClick = (group: string) => {
    const subcategoriesInGroup = codes.filter(
      (option) => option.category_title === group,
    );
    const combinedValues = [...localValues, ...subcategoriesInGroup];

    setLocalValues(combinedValues);

    onSelect(
      combinedValues.map((v) => v.category_title.concat(v.sub_category_title)),
    );
  };

  return (
    <Autocomplete
      id="category-type-lookup"
      options={codes}
      groupBy={(option) => option.category_title}
      getOptionLabel={(option) => option.sub_category_title}
      renderInput={(params) => (
        <TextField
          sx={{
            "& .MuiInputLabel-root": {
              color: "black",
            },
          }}
          {...params}
          label="Category"
        />
      )}
      multiple
      value={localValues}
      onChange={(_, value) => {
        setLocalValues(value || []);
        onSelect(
          value
            ? value.map((v) => v.sub_category_title)
            : undefined
        );
      }}
      renderGroup={(params) => (
        <SectionHeader
          key={params.key}
          group={params.group}
          children={params.children}
          onClick={() => handleSectionHeaderClick(params.group)}
        />
      )}
      disableCloseOnSelect
      clearOnBlur={false}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps}>
            <Typography>{option.sub_category_title}</Typography>
          </li>
        );
      }}
      isOptionEqualToValue={(option, value) => 
        option.sub_category_id === value.sub_category_id
      }
    />
  );
};

export default CategorySelect;

interface SectionHeaderProps {
  group: string;
  children: React.ReactNode;
  onClick: VoidFunction;
}

const SectionHeader = ({ group, children, onClick }: SectionHeaderProps) => (
  <Box key={group}>
    <MenuItem onClick={onClick}>
      <Typography variant="h6">{group}</Typography>
    </MenuItem>
    {children}
  </Box>
);

interface OriginalCategory {
  id: number;
  title: string;
  sub_categories: Array<{
    id: number;
    title: string;
  }>;
}

interface TransformedCategory {
  category_id: number;
  category_title: string;
  sub_category_title: string;
  sub_category_id: number;
}

function transformData(current: OriginalCategory[]): TransformedCategory[] {
  const newData: TransformedCategory[] = [];

  for (const category of current) {
    const { id: category_id, title: category_title, sub_categories } = category;

    for (const subCategory of sub_categories) {
      const { id: sub_category_id, title: sub_category_title } = subCategory;
      newData.push({
        category_id,
        category_title: toTitleCase(category_title),
        sub_category_id,
        sub_category_title: toTitleCase(sub_category_title),
      });
    }
  }

  return newData;
}
